/**
 * Tiny safe Markdown renderer for in-app docs (README + future
 * narrow renderings). Intentionally minimal — no HTML pass-through,
 * no script tags, no embedded React. Supports the subset emitted by
 * our docs build:
 *   - `# … #### ` headings
 *   - paragraphs (with **bold**, *italic*, `code`, [text](url))
 *   - `- ` / `* ` bullets and `1. ` ordered lists
 *   - pipe tables (with `| --- |` separator row)
 *   - fenced code blocks (``` … ```)
 *   - `---` horizontal rule
 *   - `> ` blockquote
 *
 * Anything else falls through as plain text, escaped. URLs are only
 * rendered as anchors when the protocol is http / https / mailto.
 */
import React from 'react';

const ESC_HTML = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ESC_HTML[c]);

const SAFE_URL = /^(https?:\/\/|mailto:|\/)/i;

function renderInline(text, keyPrefix) {
  // Escape first, then re-introduce safe inline tokens.
  let html = escapeHtml(text);
  // inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  // bold + italic
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  // links — only safe protocols
  html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, t, u) => {
    if (!SAFE_URL.test(u)) return escapeHtml(t);
    return `<a href="${escapeHtml(u)}" target="_blank" rel="noopener noreferrer">${t}</a>`;
  });
  // eslint-disable-next-line react/no-danger
  return <span key={keyPrefix} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function MarkdownView({ source, testid = 'markdown-view' }) {
  if (!source || typeof source !== 'string') {
    return <div data-testid={`${testid}-empty`} />;
  }
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let key = 0;
  const k = () => `md-${key++}`;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — flush.
    if (!line.trim()) { i++; continue; }

    // Fenced code block.
    if (line.startsWith('```')) {
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        buf.push(lines[i]); i++;
      }
      i++; // skip closing fence
      out.push(<pre key={k()} className="qf-md-code"><code>{buf.join('\n')}</code></pre>);
      continue;
    }

    // Horizontal rule.
    if (/^---+\s*$/.test(line)) {
      out.push(<hr key={k()} className="qf-md-hr" />);
      i++; continue;
    }

    // Heading.
    const h = line.match(/^(#{1,4})\s+(.+)$/);
    if (h) {
      const level = h[1].length;
      const Tag = `h${Math.min(level + 1, 6)}`; // map # -> h2 to keep page h1 unique
      out.push(<Tag key={k()} className={`qf-md-h${level}`}>{renderInline(h[2], k())}</Tag>);
      i++; continue;
    }

    // Blockquote.
    if (line.startsWith('> ')) {
      const buf = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        buf.push(lines[i].slice(2));
        i++;
      }
      out.push(
        <blockquote key={k()} className="qf-md-quote">
          {renderInline(buf.join(' '), k())}
        </blockquote>,
      );
      continue;
    }

    // Pipe table.
    if (line.startsWith('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
      const headerCells = line.split('|').map((s) => s.trim()).filter(Boolean);
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        const cells = lines[i].split('|').map((s) => s.trim());
        // Remove leading/trailing empty cells produced by surrounding `|`.
        if (cells.length && cells[0] === '') cells.shift();
        if (cells.length && cells[cells.length - 1] === '') cells.pop();
        rows.push(cells);
        i++;
      }
      out.push(
        <div key={k()} className="qf-md-table-wrap">
          <table className="qf-md-table">
            <thead>
              <tr>{headerCells.map((c, ix) => <th key={ix}>{renderInline(c, k())}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, rix) => (
                <tr key={rix}>
                  {row.map((c, cix) => <td key={cix}>{renderInline(c, k())}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Unordered list.
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      out.push(
        <ul key={k()} className="qf-md-list">
          {items.map((it, ix) => <li key={ix}>{renderInline(it, k())}</li>)}
        </ul>,
      );
      continue;
    }

    // Ordered list.
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      out.push(
        <ol key={k()} className="qf-md-list">
          {items.map((it, ix) => <li key={ix}>{renderInline(it, k())}</li>)}
        </ol>,
      );
      continue;
    }

    // Paragraph — collapse consecutive non-empty lines.
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^[#>\-*\d|`]/.test(lines[i])) {
      buf.push(lines[i]); i++;
    }
    out.push(<p key={k()} className="qf-md-p">{renderInline(buf.join(' '), k())}</p>);
  }

  return <div data-testid={testid} className="qf-md-view">{out}</div>;
}
